import React, { Component } from 'react';
import { withTranslation, Translation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Glyphicon } from 'react-bootstrap';
import { titleize, unescapeHTML } from 'underscore.string';
import CustomerSearch from 'components/site/customer-search';
import LocationAutosuggest from 'components/site/location-autosuggest';
import { getCurrentStatus } from 'helpers/timeline';
import OrdersActionCreators from '../../../actions/OrdersActionCreators';
import Workflow from './Workflow';
import CancelOrderModal from './CancelOrderModal';
import Badge from '../../v2/Badge';
import { sluggify } from 'utils/localization/utils';
import { showToast } from 'stores/ToastStore';
import DropdownItem from 'components/v2/dropdowns/DropdownItem';
import DropdownMenu from 'components/v2/dropdowns/DropdownMenu';
import { OverrideBillingStatusModal } from 'components/modules/shipments/OverrideBillingStatusModal';
import SessionStore from 'stores/SessionStore';
import BillingStatusRenderer from 'components/modules/shipments/BillingStatusRenderer';
import { Menu } from '@headlessui/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
const closedStatuses = ['canceled', 'complete'];
const isBillingStatusAdmin = SessionStore.isBillingStatusAdmin();
const isBillingStatusManager = SessionStore.isBillingStatusManager();
function getBadgeColor(status) {
    if (!closedStatuses.includes(status)) {
        return 'brand';
    }
    return '';
}
const canReopen = (order) => {
    if (!order) {
        return false;
    }
    return closedStatuses.includes(order.status);
};
const canOverrideBillingStatus = () => {
    return isBillingStatusManager || isBillingStatusAdmin;
};
const isCancelable = (order) => {
    if (!order) {
        return false;
    }
    if (order.outstanding_ar !== 0 || order.outstanding_ap !== 0) {
        return false;
    }
    const shipments = order.shipments || [];
    const hasOpenShipments = shipments.find((s) => s.carrier && s.current && !s.canceled);
    return !hasOpenShipments;
};
class AddressInput extends Component {
    constructor(props) {
        super(props);
        this.handleAutosuggestedItem = (item) => {
            const placeIdObject = (item === null || item === void 0 ? void 0 : item.place_id) ? { place_id: item === null || item === void 0 ? void 0 : item.place_id } : {};
            this.setState(Object.assign({ suggestions: [item], address: item.address }, placeIdObject));
        };
        this.state = {
            suggestions: [
                { name: this.props.address.name, address: this.props.address },
            ],
            name: this.props.address ? this.props.address.name : '',
            address: this.props.address ? this.props.address.address : '',
            place_id: this.props.address ? this.props.address.place_id : undefined,
        };
    }
    render() {
        return (React.createElement(Translation, null, (t, { i18n }) => (React.createElement("div", { className: "flex flex-col" },
            React.createElement("input", { className: "w-full pl-4 pr-2 mb-2 border rounded shadow-none appearance-none border-grey h-9plus text-grey-darker", value: this.state.name, placeholder: titleize(t('NAME')), onChange: (e) => {
                    this.setState({ name: e.target.value });
                } }),
            React.createElement(LocationAutosuggest, { key: this.state.address, id: "autosuggest-address", name: this.state.name, address: this.state.address, value: this.state.address, suggestions: [
                    { name: this.props.address.name, address: this.props.address },
                ], callbackSelectedItem: this.handleAutosuggestedItem }),
            React.createElement("div", { className: "flex flex-row justify-end mt-2" },
                React.createElement("button", { className: "button button-green", onClick: () => {
                        const newAddress = {
                            name: this.state.name,
                            address: this.state.address,
                            place_id: this.state.place_id,
                        };
                        OrdersActionCreators.updateOrder(this.props.id, {
                            [this.props.type]: newAddress,
                        });
                        this.props.toggleEditing();
                    } }, titleize(t('SAVE'))),
                React.createElement("button", { className: "bg-white button text-grey hover:text-grey-darker", onClick: () => this.props.toggleEditing() }, titleize(t('CANCEL'))))))));
    }
}
AddressInput.propTypes = {
    address: PropTypes.shape({
        address: PropTypes.string,
        name: PropTypes.string,
    }).isRequired,
    id: PropTypes.string,
    type: PropTypes.string,
    toggleEditing: PropTypes.func.isRequired,
};
const Address = (props) => {
    if (!props.address)
        return null;
    const { address, stop_type } = props;
    return (React.createElement("div", { className: "flex-1" },
        React.createElement("div", { className: "my-1" },
            React.createElement("strong", { "data-testid": `${stop_type}-address-name` }, address.name)),
        React.createElement("div", { className: "my-1", "data-testid": `${stop_type}-address` }, address.address)));
};
Address.propTypes = {
    address: PropTypes.shape({
        address: PropTypes.string,
        name: PropTypes.string,
    }).isRequired,
};
const ShipperCarrierEdit = (props) => {
    let placeholder = '';
    if (props.company) {
        placeholder =
            props.type === 'shipper'
                ? props.company.company_name
                : props.company.name;
    }
    return (React.createElement(Translation, null, (t, { i18n }) => (React.createElement(CustomerSearch, { clearOnSelect: true, placeholder: placeholder || (props === null || props === void 0 ? void 0 : props.placeholder), assignField: true, onSelect: (item, value) => {
            // Only apply 'Trained' status validation for carriers, not shippers
            if (props.type === 'carrier' && value.status !== 'Trained')
                return showToast(t('CARRIER_IS_NOT_TRAINED'), t('CARRIER_IS_NOT_TRAINED'), 'error');
            OrdersActionCreators.updateOrder(props.id, {
                [props.type]: value._id,
            });
            props.toggleEditing();
        }, type: props.type + 's', inputStyle: {
            height: '26px',
            paddingLeft: '3px',
            paddingTop: '0px',
            paddingBottom: '0px',
        } }))));
};
ShipperCarrierEdit.propTypes = {
    type: PropTypes.string,
    company: PropTypes.object,
    id: PropTypes.string,
    toggleEditing: PropTypes.func,
};
const orderClosedStatus = ['canceled', 'complete'];
class StatusBar extends Component {
    constructor(props) {
        var _a;
        super(props);
        this.toggleEditing = (type) => {
            this.setState({
                [type]: !this.state[type],
            });
        };
        this.handleSelect = (order, key) => {
            switch (key) {
                case 'cancel':
                    this.setState({ showCancelModal: true });
                    break;
                case 'reopen':
                    OrdersActionCreators.reopenOrder(order._id);
                    break;
                case 'override_billing_status':
                    this.setState({ showOverrideBillingStatusModal: true });
                    break;
                default:
                    break;
            }
        };
        this.workflowAction = (workflow) => {
            switch (workflow) {
                case 'arrived':
                    this.props.onArrivedClick();
                    break;
                default:
                    OrdersActionCreators.transitionOrder(this.props.order, workflow);
            }
        };
        this.state = {
            isEditingArriving: false,
            isEditingDelivering: false,
            showEditArrivingIcon: false,
            showEditDeliveringIcon: false,
            isEditingShipper: false,
            isEditingCarrier: false,
            showEditShipperIcon: false,
            showEditCarrierIcon: false,
            showCancelModal: false,
            showOverrideBillingStatusModal: false,
            legManagementEnabled: ((_a = props.flags) === null || _a === void 0 ? void 0 : _a.legManagement) === true,
        };
    }
    render() {
        var _a, _b;
        if (!this.props.order) {
            return null;
        }
        const { order, shipments, t } = this.props;
        let shipperName = '';
        if (order.shipper && order.shipper.company_name) {
            shipperName = unescapeHTML(order.shipper.company_name);
        }
        let carrierName = '';
        if (order.carrier && order.carrier.name) {
            carrierName = unescapeHTML(order.carrier.name);
        }
        const status = getCurrentStatus(order.timeline);
        return (React.createElement("div", { className: "flex flex-col flex-wrap p-4 mb-1 md:flex-row sm:p-6" },
            React.createElement("div", { className: "flex-1 px-2" },
                React.createElement("div", { className: "mb-4" },
                    React.createElement("h2", { className: "my-0 max-w-max" }, order.order_id),
                    React.createElement("div", { className: "flex flex-col items-start gap-2" },
                        React.createElement(Badge, { color: getBadgeColor(status) }, titleize(t(sluggify(status)))),
                        ((_a = this.props.flags) === null || _a === void 0 ? void 0 : _a.legManagement) === true && (React.createElement(BillingStatusRenderer, Object.assign({}, this.props, { isOrderContext: true }))))),
                React.createElement("div", null,
                    React.createElement("div", null,
                        React.createElement("div", { className: "flex flex-row items-center min-h-28px", onMouseEnter: () => {
                                this.setState({ showEditShipperIcon: true });
                            }, onMouseLeave: () => {
                                this.setState({ showEditShipperIcon: false });
                            } },
                            React.createElement("span", { className: "mr-1 text-sm font-medium text-gray-500" },
                                titleize(t('SHIPPER', { count: 1 })),
                                ":"),
                            !this.state.isEditingShipper ? (React.createElement("a", { className: "no-underline", href: order.shipper ? `/users/${order.shipper._id}` : '#', hidden: !order.shipper || !order.shipper.company_name, "data-testid": "shipper-link" },
                                React.createElement("span", { className: "text-green" }, shipperName))) : (React.createElement(ShipperCarrierEdit, { id: order.id, type: "shipper", placeholder: t('SEARCH_FOR_A_SHIPPER'), company: order.shipper, toggleEditing: () => {
                                    this.toggleEditing('showEditShipperIcon');
                                    this.toggleEditing('isEditingShipper');
                                } })),
                            React.createElement("div", { className: "ml-2 mr-1" }, this.state.showEditShipperIcon ? (React.createElement(Glyphicon, { glyph: "edit", style: { fontSize: 16 }, className: "cursor-pointer", onClick: () => {
                                    this.toggleEditing('isEditingShipper');
                                } })) : (React.createElement("span", null, "\u00A0\u00A0\u00A0\u00A0"))))),
                    React.createElement("div", { className: "mt-1" },
                        React.createElement("div", { className: "flex flex-row items-center min-h-28px", onMouseEnter: () => {
                                this.setState({ showEditCarrierIcon: true });
                            }, onMouseLeave: () => {
                                this.setState({ showEditCarrierIcon: false });
                            } },
                            React.createElement("span", { className: "mr-1 text-sm font-medium text-gray-500" },
                                titleize(t('CARRIER', { count: 1 })),
                                ":"),
                            !this.state.isEditingCarrier ? (React.createElement("a", { className: "no-underline", href: order.carrier ? `/users/${order.carrier._id}` : '#', hidden: !order.carrier || !order.carrier.name, "data-testid": "carrier-link" },
                                React.createElement("div", { className: "relative" },
                                    React.createElement("span", { className: "text-green" }, carrierName),
                                    order.carrier && order.carrier.mvp ? (React.createElement("img", { src: "/img/cargomatic-circle.png", className: "absolute -top-1.5 -right-3 w-3", alt: "MVP" })) : null))) : (React.createElement(ShipperCarrierEdit, { id: order.id, type: "carrier", placeholder: t('SEARCH_FOR_A_CARRIER'), company: order.carrier, toggleEditing: () => {
                                    this.toggleEditing('showEditCarrierIcon');
                                    this.toggleEditing('isEditingCarrier');
                                } })),
                            React.createElement("div", { className: "ml-2 mr-1" }, this.state.showEditCarrierIcon ? (React.createElement(Glyphicon, { glyph: "edit", style: { fontSize: 16 }, className: "cursor-pointer", onClick: () => {
                                    this.toggleEditing('isEditingCarrier');
                                } })) : (React.createElement("span", null, "\u00A0\u00A0\u00A0\u00A0"))))),
                    React.createElement("div", { className: "flex flex-row items-center mt-1 font-bold min-h-28px" },
                        React.createElement("span", { className: "mr-1 text-sm font-medium text-gray-500" },
                            titleize(t('MARKET', { count: 1 })),
                            ":"),
                        React.createElement("span", { className: "font-normal text-green" }, order.arriving && order.arriving.length
                            ? order.arriving[0].market.code
                            : null)))),
            React.createElement("div", { className: "flex-1 p-2", onMouseEnter: () => this.setState({ showEditArrivingIcon: true }), onMouseLeave: () => this.setState({ showEditArrivingIcon: false }) },
                React.createElement("div", { className: "flex-col h-full" },
                    React.createElement("div", { className: "flex items-start justify-between mb-2" },
                        React.createElement("h3", { className: "mt-0 mb-4" }, titleize(t('ORIGIN'))),
                        React.createElement("div", { className: "mt-1 sm:mr-1" }, this.state.showEditArrivingIcon &&
                            !this.state.isEditingArriving ? (React.createElement(Glyphicon, { glyph: "edit", style: { fontSize: 20 }, className: "cursor-pointer", onClick: () => {
                                this.toggleEditing('isEditingArriving');
                            } })) : (React.createElement("span", null, "\u00A0\u00A0\u00A0\u00A0")))),
                    order.arriving && order.arriving.length ? (!this.state.isEditingArriving ? (React.createElement(Address, { id: order.id, address: order.arriving[0], type: "arriving", stop_type: "origin" })) : (React.createElement(AddressInput, { id: order.id, type: "arriving", address: order.arriving[0], toggleEditing: () => {
                            this.toggleEditing('isEditingArriving');
                            this.toggleEditing('showEditArrivingIcon');
                        } }))) : !this.state.isEditingArriving ? (React.createElement("a", { className: "text-green", href: "#", onClick: () => {
                            this.toggleEditing('isEditingArriving');
                        } }, t('ADD_ORIGIN_ADDRESS'))) : (React.createElement(AddressInput, { id: order.id, type: "arriving", toggleEditing: () => {
                            this.toggleEditing('isEditingArriving');
                            this.toggleEditing('showEditArrivingIcon');
                        } })))),
            React.createElement("div", { className: "flex-1 p-2", onMouseEnter: () => this.setState({ showEditDeliveringIcon: true }), onMouseLeave: () => this.setState({ showEditDeliveringIcon: false }) },
                React.createElement("div", { className: "flex-col h-full" },
                    React.createElement("div", { className: "flex items-start justify-between mb-2" },
                        React.createElement("h3", { className: "mt-0 mb-4" }, titleize(t('DESTINATION'))),
                        React.createElement("div", { className: "mt-1 sm:mr-1" }, this.state.showEditDeliveringIcon &&
                            !this.state.isEditingDelivering ? (React.createElement(Glyphicon, { glyph: "edit", style: { fontSize: 20 }, className: "cursor-pointer", onClick: () => {
                                this.toggleEditing('isEditingDelivering');
                            } })) : (React.createElement("span", null, "\u00A0\u00A0\u00A0\u00A0")))),
                    order.delivering && order.delivering.length ? (!this.state.isEditingDelivering ? (React.createElement(Address, { id: order.id, address: order.delivering[0], type: "delivering", stop_type: "destination" })) : (React.createElement(AddressInput, { id: order.id, address: order.delivering[0], type: "delivering", toggleEditing: () => {
                            this.toggleEditing('isEditingDelivering');
                            this.toggleEditing('showEditDeliveringIcon');
                        } }))) : !this.state.isEditingDelivering ? (React.createElement("a", { className: "text-green", href: "#", onClick: () => {
                            this.toggleEditing('isEditingDelivering');
                        } }, t('ADD_DESTINATION_ADDRESS'))) : (React.createElement(AddressInput, { id: order.id, type: "delivering", toggleEditing: () => {
                            this.toggleEditing('isEditingDelivering');
                            this.toggleEditing('showEditDeliveringIcon');
                        } })))),
            React.createElement("div", { className: "flex-1 px-2" },
                React.createElement("div", { className: "flex-col h-full" },
                    React.createElement("div", { className: "inline-flex ml-px" },
                        !orderClosedStatus.includes(order.status) && (React.createElement(Workflow, { order: order, shipments: shipments, onTransition: (step) => this.workflowAction(step) })),
                        React.createElement(DropdownMenu, { dataTestId: "DropdownMenu-d909fd8e", id: "bulk-actions-dropdown", title: t('ACTIONS'), className: `mr-2` },
                            React.createElement(Menu.Item, { "data-testid": "MenuItem-09989fbd", key: "reopen" },
                                React.createElement(DropdownItem, { dataTestId: "reopenOrder", disabled: !canReopen(order), onClick: () => this.handleSelect(order, 'reopen') }, t('RE_OPEN'))),
                            React.createElement(Menu.Item, { "data-testid": "MenuItem-09989fbd", key: "cancel" },
                                React.createElement(DropdownItem, { dataTestId: "cancelOrder", disabled: !isCancelable(order), onClick: () => this.handleSelect(order, 'cancel') }, titleize(t('CANCEL')))),
                            ((_b = this.props.flags) === null || _b === void 0 ? void 0 : _b.legManagement) === true && (React.createElement(Menu.Item, { "data-testid": "MenuItem-09989fbd", key: "override_billing_status" },
                                React.createElement(DropdownItem, { dataTestId: "cancelOrder", disabled: !canOverrideBillingStatus(order), onClick: () => this.handleSelect(order, 'override_billing_status') }, titleize(t('OVERRIDE_BILLING_STATUS'))))))))),
            this.state.showCancelModal && (React.createElement(CancelOrderModal, { order: order, show: this.state.showCancelModal, onHide: () => this.setState({ showCancelModal: false }) })),
            canOverrideBillingStatus() &&
                this.state.showOverrideBillingStatusModal && (React.createElement(OverrideBillingStatusModal, { shipmentIds: [], orderId: order.id, open: this.state.showOverrideBillingStatusModal, onHide: () => this.setState({ showOverrideBillingStatusModal: false }), onSuccess: () => {
                    OrdersActionCreators.getOrder(order._id);
                    this.setState({ showOverrideBillingStatusModal: false });
                } }))));
    }
}
StatusBar.propTypes = {
    order: PropTypes.object,
    onArrivedClick: PropTypes.func.isRequired,
    flags: PropTypes.object,
};
// Higher-Order Component to inject flags from LaunchDarkly hook
function withFlags(Component) {
    return function WrappedComponent(props) {
        const flags = useFlags();
        return React.createElement(Component, Object.assign({}, props, { flags: flags }));
    };
}
export default withTranslation()(withFlags(StatusBar));
